import React, { useState, useEffect } from 'react';
import { BlobItem } from './types';
import { BlobStorageService } from './BlobStorageService';
import {
  LoadingSpinner,
  ErrorDisplay,
  Header,
  BreadcrumbNavigation,
  FileTable,
  EmptyState,
  Pagination
} from './components';

// Import Montserrat font
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';

const config = {
  accountName: 'hebifiles',
  containerName: '$web',
  publicDirectory: 'download/',
};

const blobService = new BlobStorageService(config);

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

interface BreadcrumbItem {
  name: string;
  path: string;
  isLast: boolean;
}

type SortField = 'name' | 'lastModified' | 'size';
type SortDirection = 'asc' | 'desc';

const BlobBrowser: React.FC = () => {
  // Core data states
  const [items, setItems] = useState<BlobItem[]>([]); // Currently displayed blob items (files/folders)
  const [isLoading, setIsLoading] = useState(true); // Loading state for initial data fetch
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track if this is the first load from URL
  const [error, setError] = useState<string | null>(null); // Error message if any operation fails
  const [currentPrefix, setCurrentPrefix] = useState(''); // Current folder path/prefix being browsed

  // Prefetching states
  const [prefetchedItems, setPrefetchedItems] = useState<BlobItem[]>([]); // All prefetched items for better performance
  const [isPrefetching, setIsPrefetching] = useState(false); // Whether we're currently prefetching all items
  const [prefetchedPrefix, setPrefetchedPrefix] = useState(''); // The prefix for which we have prefetched data
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0); // Current page number (0-based)
  const [hasNextPage, setHasNextPage] = useState(false); // Whether more pages are available
  const [continuationTokens, setContinuationTokens] = useState<string[]>([]); // Azure blob continuation tokens for server-side pagination
  const [pageSize, setPageSize] = useState(25); // Number of items to display per page
  
  // Sorting states
  const [sortField, setSortField] = useState<SortField>('name'); // Field to sort by (name, lastModified, size)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc'); // Sort direction (ascending/descending)
  
  // Search state
  const [searchQuery, setSearchQuery] = useState(''); // Current search/filter query

  /**
   * Sorts an array of blob items according to specified field and direction
   * Directories are always sorted first, regardless of sort criteria
   * @param items - Array of blob items to sort
   * @param field - Field to sort by (name, lastModified, size)
   * @param direction - Sort direction (asc or desc)
   * @returns Sorted array of blob items
   */
  const sortItems = (items: BlobItem[], field: SortField, direction: SortDirection): BlobItem[] => {
    return [...items].sort((a, b) => {
      // Always put directories first regardless of sort field
      if (a.isDirectory && !b.isDirectory) return -1;
      if (!a.isDirectory && b.isDirectory) return 1;
      
      let aValue: string | number;
      let bValue: string | number;
      
      switch (field) {
        case 'name':
          // Convert to lowercase for case-insensitive sorting
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'lastModified':
          // Handle undefined dates by treating them as the oldest possible date (epoch)
          aValue = a.lastModified ? a.lastModified.getTime() : 0;
          bValue = b.lastModified ? b.lastModified.getTime() : 0;
          // If both items have no date, fall back to name sorting
          if (aValue === 0 && bValue === 0) {
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
          }
          break;
        case 'size':
          // Handle undefined sizes by treating them as 0 bytes
          aValue = a.contentLength ?? 0;
          bValue = b.contentLength ?? 0;
          // If both items have no size (e.g., directories), fall back to name sorting
          if (aValue === 0 && bValue === 0) {
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
          }
          break;
        default:
          return 0;
      }
      
      // Apply sort direction
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  /**
   * Filters blob items based on a search query
   * Performs case-insensitive name matching
   * @param items - Array of blob items to filter
   * @param query - Search query string
   * @returns Filtered array of blob items
   */
  const filterItems = (items: BlobItem[], query: string): BlobItem[] => {
    if (!query.trim()) return items;
    
    const searchTerm = query.toLowerCase();
    return items.filter(item => 
      item.name.toLowerCase().includes(searchTerm)
    );
  };

  /**
   * Handles changes to the search input field
   * Resets pagination to first page when search query changes
   * @param query - New search query string
   */
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    // Reset to first page when search query changes
    setCurrentPage(0);
  };

  /**
   * Handles sorting column header clicks
   * Toggles sort direction if same field is clicked again, otherwise sets new field with ascending direction
   * @param field - Sort field to apply
   */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
    // Don't reload data from server, just re-sort current items
  };

  /**
   * Prefetches all items in a directory
   * Avoids re-prefetching if data is already available for the same prefix
   * @param prefix - Directory prefix to prefetch
   * @returns Promise resolving to sorted array of all items
   */
  const prefetchAllItems = async (prefix: string) => {
    // Only prefetch if we haven't already prefetched this prefix
    if (prefix === prefetchedPrefix && prefetchedItems.length > 0) {
      return prefetchedItems;
    }

    setIsPrefetching(true);
    try {
      // Fetch all items from the blob service
      const allItems = await blobService.listAllBlobs(prefix);
      const sortedItems = sortItems(allItems, sortField, sortDirection);
      setPrefetchedItems(sortedItems);
      setPrefetchedPrefix(prefix);
      return sortedItems;
    } catch (err) {
      throw err;
    } finally {
      setIsPrefetching(false);
    }
  };

  /**
   * Performs client-side pagination on an array of items
   * @param items - Array of items to paginate
   * @param page - Page number (0-based)
   * @param pageSize - Number of items per page
   * @returns Object containing paginated items and pagination metadata
   */
  const getPaginatedItems = (items: BlobItem[], page: number, pageSize: number) => {
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = items.slice(startIndex, endIndex);
    const hasNextPage = endIndex < items.length;
    
    return {
      items: paginatedItems,
      hasNextPage
    };
  };

  /**
   * Calculates total number of pages for current data set
   * Returns null for server-side pagination where total is unknown
   * @returns Total number of pages or null if unknown
   */
  const getTotalPages = () => {
    if (currentPrefix === prefetchedPrefix && prefetchedItems.length > 0) {
      // Use prefetched data to calculate total pages
      const totalItems = searchQuery.trim() 
        ? filterItems(prefetchedItems, searchQuery).length 
        : prefetchedItems.length;
      return Math.ceil(totalItems / pageSize);
    }
    // For server-side pagination, we don't know the total pages
    return null;
  };

  /**
   * Main function to load blob items from Azure Storage
   * Attempts to use prefetched data for better performance, falls back to server-side pagination
   * @param prefix - Directory prefix to load (default: root)
   * @param page - Page number to load (default: 0)
   * @param tokens - Continuation tokens for server-side pagination
   * @param customPageSize - Override default page size
   */
  const loadBlobs = async (prefix: string = '', page: number = 0, tokens: string[] = [], customPageSize?: number) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const actualPageSize = customPageSize || pageSize;
      
      // Check if we have prefetched data for this prefix
      if (prefix === prefetchedPrefix && prefetchedItems.length > 0) {
        // Use prefetched data with client-side pagination for better performance
        const sortedItems = sortItems(prefetchedItems, sortField, sortDirection);
        const paginatedResult = getPaginatedItems(sortedItems, page, actualPageSize);
        
        setItems(paginatedResult.items);
        setHasNextPage(paginatedResult.hasNextPage);
        setContinuationTokens([]); // Clear continuation tokens as we're using client-side pagination
      } else {
        // Try to prefetch all items first for better user experience
        try {
          const allPrefetchedItems = await prefetchAllItems(prefix);
          const paginatedResult = getPaginatedItems(allPrefetchedItems, page, actualPageSize);
          
          setItems(paginatedResult.items);
          setHasNextPage(paginatedResult.hasNextPage);
          setContinuationTokens([]); // Clear continuation tokens as we're using client-side pagination
        } catch (prefetchError) {
          // If prefetching fails (e.g., too many items), fall back to server-side pagination
          console.warn('Prefetching failed, falling back to server-side pagination:', prefetchError);
          
          // Use Azure blob continuation tokens for server-side pagination
          const continuationToken = page > 0 ? tokens[page - 1] : undefined;
          const result = await blobService.listBlobs(prefix, actualPageSize, continuationToken);
          
          // Always sort the items according to current sort criteria
          const sortedItems = sortItems(result.items, sortField, sortDirection);
          
          setItems(sortedItems);
          setHasNextPage(result.hasNextPage);
          
          // Update continuation tokens for pagination
          if (result.hasNextPage && result.nextContinuationToken) {
            setContinuationTokens(prev => {
              const newTokens = [...prev];
              newTokens[page] = result.nextContinuationToken!;
              return newTokens;
            });
          }
        }
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      
      // Handle specific error types with user-friendly messages
      if (errorMessage === 'DIRECTORY_NOT_FOUND') {
        setError('DIRECTORY_NOT_FOUND');
      } else if (errorMessage === 'FILE_PREFIX_NOT_FOUND') {
        setError('FILE_PREFIX_NOT_FOUND');
      } else if (errorMessage === 'CONTAINER_NOT_FOUND') {
        setError('CONTAINER_NOT_FOUND');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
      // Mark initial load as complete after first successful load
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    }
  };

  // Effect hook for initial component setup and URL parsing
  useEffect(() => {
    // Get the current prefix from URL parameters or blob service
    const rawPrefix = blobService.getPrefix();
    
    // Normalize the prefix by removing all leading/trailing slashes
    let normalizedPrefix = rawPrefix;
    
    // Remove all leading slashes
    while (normalizedPrefix.startsWith('/')) {
      normalizedPrefix = normalizedPrefix.substring(1);
    }
    
    // Remove multiple trailing slashes (but keep one if it was a directory)
    if (normalizedPrefix.length > 1) {
      while (normalizedPrefix.endsWith('//')) {
        normalizedPrefix = normalizedPrefix.slice(0, -1);
      }
    }
    
    // If the URL was malformed and we normalized it, redirect to the clean URL
    if (rawPrefix !== normalizedPrefix) {
      const url = new URL(window.location.href);
      const baseUrl = `${url.protocol}//${url.host}${url.pathname}`;
      
      let newUrl;
      if (normalizedPrefix) {
        newUrl = `${baseUrl}?prefix=${normalizedPrefix}`;
      } else {
        newUrl = baseUrl; // Root directory
      }
      
      // Redirect to the normalized URL
      window.history.replaceState({}, '', newUrl);
    }

    // If the final prefix is empty '', set it to public directory
    if (normalizedPrefix === '') {
      normalizedPrefix = config.publicDirectory;
    }
    
    setCurrentPrefix(normalizedPrefix);
    setCurrentPage(0);
    setContinuationTokens([]);
    loadBlobs(normalizedPrefix, 0, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array for initial load only

  // Effect hook to handle browser back/forward navigation
  useEffect(() => {
    /**
     * Handles browser popstate events (back/forward navigation)
     * Updates component state to match the new URL with normalization
     */
    const handlePopState = () => {
      const rawPrefix = blobService.getPrefix();
      
      // Normalize the prefix by removing all leading/trailing slashes
      let normalizedPrefix = rawPrefix;
      
      // Remove all leading slashes
      while (normalizedPrefix.startsWith('/')) {
        normalizedPrefix = normalizedPrefix.substring(1);
      }
      
      // Remove multiple trailing slashes (but keep one if it was a directory)
      if (normalizedPrefix.length > 1) {
        while (normalizedPrefix.endsWith('//')) {
          normalizedPrefix = normalizedPrefix.slice(0, -1);
        }
      }
      
      // If the URL was malformed and we normalized it, redirect to the clean URL
      if (rawPrefix !== normalizedPrefix) {
        const url = new URL(window.location.href);
        const baseUrl = `${url.protocol}//${url.host}${url.pathname}`;
        
        let newUrl;
        if (normalizedPrefix) {
          newUrl = `${baseUrl}?prefix=${normalizedPrefix}`;
        } else {
          newUrl = baseUrl; // Root directory
        }
        
        // Redirect to the normalized URL
        window.history.replaceState({}, '', newUrl);
      }

      // If the final prefix is empty '', set it to public directory
      if (normalizedPrefix === '') {
        normalizedPrefix = config.publicDirectory;
      }
      
      setCurrentPrefix(normalizedPrefix);
      setSearchQuery(''); // Clear search when navigating via browser
      setPrefetchedItems([]); // Clear prefetched data when navigating
      setPrefetchedPrefix(''); // Reset prefetched prefix
      setCurrentPage(0);
      setContinuationTokens([]);
      loadBlobs(normalizedPrefix, 0, []);
    };

    // Add event listener for browser navigation
    window.addEventListener('popstate', handlePopState);
    
    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - this should only be set up once

  // Effect hook to re-sort items when sort criteria changes
  useEffect(() => {
    // If we have prefetched data, sort the entire list and update pagination
    if (currentPrefix === prefetchedPrefix && prefetchedItems.length > 0) {
      const sortedItems = sortItems(prefetchedItems, sortField, sortDirection);
      const filteredItems = searchQuery.trim() 
        ? filterItems(sortedItems, searchQuery) 
        : sortedItems;
      const paginatedResult = getPaginatedItems(filteredItems, currentPage, pageSize);
      setItems(paginatedResult.items);
      setHasNextPage(paginatedResult.hasNextPage);
    } else if (items.length > 0) {
      // Fallback for server-side pagination - only sort current page items
      const sortedItems = sortItems(items, sortField, sortDirection);
      // Only update if the sorting actually changed the order to avoid unnecessary re-renders
      if (JSON.stringify(sortedItems) !== JSON.stringify(items)) {
        setItems(sortedItems);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection]);

  // Effect hook to handle search query changes and update displayed items
  useEffect(() => {
    // When search query changes, update the displayed items and pagination
    if (currentPrefix === prefetchedPrefix && prefetchedItems.length > 0) {
      const sortedItems = sortItems(prefetchedItems, sortField, sortDirection);
      const filteredItems = searchQuery.trim() 
        ? filterItems(sortedItems, searchQuery) 
        : sortedItems;
      const paginatedResult = getPaginatedItems(filteredItems, currentPage, pageSize);
      setItems(paginatedResult.items);
      setHasNextPage(paginatedResult.hasNextPage);
    }
  }, [searchQuery]);

  /**
   * Handles navigation to folders or file downloads
   * @param path - The path/name of the item to navigate to
   * @param isDirectory - Whether the item is a folder (true) or file (false)
   */
  const handleNavigate = (path: string, isDirectory: boolean) => {
    if (isDirectory) {
      // Build the new prefix by combining current prefix with the clicked folder
      let newPrefix: string;
      if (!currentPrefix) {
        // We're at root, so the new prefix is just the folder name
        newPrefix = path;
      } else {
        // We're in a subdirectory, so append the folder name
        // But first, we need to handle the case where the current prefix 
        // might be a filename prefix rather than a directory
        
        // If the current prefix doesn't end with '/', it might be a filename prefix
        if (!currentPrefix.endsWith('/')) {
          // Find the last directory separator in the current prefix
          const lastSlashIndex = currentPrefix.lastIndexOf('/');
          if (lastSlashIndex >= 0) {
            // Keep only the directory part
            const directoryPart = currentPrefix.substring(0, lastSlashIndex + 1);
            newPrefix = directoryPart + path;
          } else {
            // No directory separator, so we're at root level with a filename prefix
            newPrefix = path;
          }
        } else {
          // Current prefix is a proper directory, append normally
          newPrefix = currentPrefix + path;
        }
      }
      
      // Ensure directory prefixes end with a slash for Azure blob hierarchy
      if (!newPrefix.endsWith('/')) {
        newPrefix += '/';
      }
      
      // Update browser URL without page reload
      const url = new URL(window.location.href);
      // Manually construct the URL to avoid encoding forward slashes
      const baseUrl = `${url.protocol}//${url.host}${url.pathname}`;
      const newUrl = `${baseUrl}?prefix=${newPrefix}`;
      window.history.pushState({}, '', newUrl);
      
      // Update component state and load new directory
      setCurrentPrefix(newPrefix);
      setSearchQuery(''); // Clear search when navigating to a new folder
      setPrefetchedItems([]); // Clear prefetched data when navigating
      setPrefetchedPrefix(''); // Reset prefetched prefix
      setCurrentPage(0);
      setContinuationTokens([]);
      loadBlobs(newPrefix, 0, []);
    } else {
      // For files, create download link and open in new tab
      const item = items.find(i => i.name === path);
      if (item?.url) {
        window.open(item.url, '_blank');
      }
    }
  };

  /**
   * Handles page navigation in pagination
   * Automatically scrolls table to top and loads appropriate data
   * @param newPage - Page number to navigate to (0-based)
   */
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    
    // Scroll table to top when changing pages for better UX
    const tableContainer = document.getElementById('blob-browser-content');
    if (tableContainer) {
      tableContainer.scrollTop = 0;
    }
    
    // If we have prefetched data, use client-side pagination for better performance
    if (currentPrefix === prefetchedPrefix && prefetchedItems.length > 0) {
      const sortedItems = sortItems(prefetchedItems, sortField, sortDirection);
      const filteredItems = searchQuery.trim() 
        ? filterItems(sortedItems, searchQuery) 
        : sortedItems;
      const paginatedResult = getPaginatedItems(filteredItems, newPage, pageSize);
      setItems(paginatedResult.items);
      setHasNextPage(paginatedResult.hasNextPage);
    } else {
      // Fall back to server-side pagination
      loadBlobs(currentPrefix, newPage, continuationTokens);
    }
  };

  /**
   * Handles changes to page size dropdown
   * Resets to first page and reloads data with new page size
   * @param newPageSize - Number of items to display per page
   */
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0); // Reset to first page
    setContinuationTokens([]); // Clear pagination tokens
    loadBlobs(currentPrefix, 0, [], newPageSize);
  };

  /**
   * Navigates to the parent directory
   * Updates URL and component state, clears search and prefetched data
   */
  const handleGoBack = () => {
    const parentPrefix = blobService.getParentPrefix(currentPrefix);
    const url = new URL(window.location.href);
    const baseUrl = `${url.protocol}//${url.host}${url.pathname}`;
    
    // Construct new URL with parent prefix or root
    let newUrl;
    if (parentPrefix) {
      newUrl = `${baseUrl}?prefix=${parentPrefix}`;
    } else {
      newUrl = baseUrl; // Root directory
    }
    
    // Update browser history and component state
    window.history.pushState({}, '', newUrl);
    setCurrentPrefix(parentPrefix);
    setSearchQuery(''); // Clear search when going back
    setPrefetchedItems([]); // Clear prefetched data when navigating
    setPrefetchedPrefix(''); // Reset prefetched prefix
    setCurrentPage(0);
    setContinuationTokens([]);
    loadBlobs(parentPrefix, 0, []);
  };

  /**
   * Generates breadcrumb navigation items from current prefix
   * Handles both directory prefixes and filename prefixes
   * Excludes the last directory if it doesn't exist (DIRECTORY_NOT_FOUND error)
   * @returns Array of breadcrumb items for navigation
   */
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    if (!currentPrefix) return []; // No breadcrumbs for root directory
    
    // If the prefix doesn't end with '/', the last part is a filename prefix, not a directory
    const isDirectoryPrefix = currentPrefix.endsWith('/');
    
    // Remove trailing slash for processing path parts
    const cleanPrefix = currentPrefix.endsWith('/') ? currentPrefix.slice(0, -1) : currentPrefix;
    const parts = cleanPrefix.split('/').filter(Boolean); // Remove empty parts
    const breadcrumbs: BreadcrumbItem[] = [];
    
    // If it's a directory prefix, include all parts
    // If it's a filename prefix, exclude the last part (the filename prefix)
    let partsToInclude = isDirectoryPrefix ? parts.length : parts.length - 1;
    
    // If there's a DIRECTORY_NOT_FOUND error and we have a directory prefix,
    // exclude the last directory from breadcrumbs since it doesn't exist
    if (error === 'DIRECTORY_NOT_FOUND' && isDirectoryPrefix && partsToInclude > 0) {
      partsToInclude = partsToInclude - 1;
    }
    
    // Build breadcrumb items from path parts
    for (let i = 0; i < partsToInclude; i++) {
      const path = parts.slice(0, i + 1).join('/') + '/';
      breadcrumbs.push({
        name: parts[i],
        path,
        isLast: i === partsToInclude - 1, // Mark the last breadcrumb for styling
      });
    }
    
    return breadcrumbs;
  };

  /**
   * Truncates breadcrumbs to fit within display limits
   * Uses ellipsis when breadcrumb path becomes too long
   * @param breadcrumbs - Full array of breadcrumb items
   * @returns Truncated breadcrumbs with ellipsis when needed
   */
  const getTruncatedBreadcrumbs = (breadcrumbs: BreadcrumbItem[]): (BreadcrumbItem | { isEllipsis: true })[] => {
    if (breadcrumbs.length === 0) return [];
    
    // Calculate total length including "download" prefix, separators, and all breadcrumb names
    const baseLength = 'download'.length;
    const separatorLength = ' > '.length; // Approximate length of separator with spaces
    
    /**
     * Calculates the total display length of breadcrumbs
     * @param crumbs - Array of breadcrumb items to measure
     * @returns Total character length needed for display
     */
    const calculateLength = (crumbs: BreadcrumbItem[]) => {
      return baseLength + crumbs.reduce((acc, crumb) => {
        return acc + separatorLength + crumb.name.length;
      }, 0);
    };
    
    // If total length is within limit, return all breadcrumbs
    if (calculateLength(breadcrumbs) <= 80) {
      return breadcrumbs;
    }
    
    // If we need to truncate, try removing breadcrumbs from the middle one by one
    // Always keep first and last breadcrumb if possible
    if (breadcrumbs.length <= 2) {
      return breadcrumbs; // Can't truncate further
    }
    
    const ellipsisLength = 3; // "..." character count
    const firstCrumb = breadcrumbs[0];
    const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
    
    // Try progressive truncation: remove middle breadcrumbs one by one until it fits
    for (let removeCount = 1; removeCount < breadcrumbs.length - 1; removeCount++) {
      // Calculate how many breadcrumbs to keep from the beginning and end
      const keepFromStart = Math.max(1, Math.floor((breadcrumbs.length - removeCount) / 2));
      const keepFromEnd = Math.max(1, breadcrumbs.length - removeCount - keepFromStart);
      
      if (keepFromStart + keepFromEnd >= breadcrumbs.length) {
        continue; // This combination doesn't make sense, try next iteration
      }
      
      // Create the truncated array with ellipsis in the middle
      const truncated = [
        ...breadcrumbs.slice(0, keepFromStart),
        { isEllipsis: true as const },
        ...breadcrumbs.slice(-keepFromEnd).map((crumb, index, arr) => 
          index === arr.length - 1 ? { ...crumb, isLast: true } : crumb
        )
      ];
      
      // Calculate length with ellipsis included
      const visibleCrumbs = [...breadcrumbs.slice(0, keepFromStart), ...breadcrumbs.slice(-keepFromEnd)];
      const lengthWithEllipsis = baseLength + separatorLength + ellipsisLength + 
        visibleCrumbs.reduce((acc, crumb) => acc + separatorLength + crumb.name.length, 0);
      
      if (lengthWithEllipsis <= 80) {
        return truncated;
      }
    }
    
    // If we still can't fit, fall back to minimal "first ... last" pattern
    const minimalLength = baseLength + separatorLength + firstCrumb.name.length + 
                         separatorLength + ellipsisLength + 
                         separatorLength + lastCrumb.name.length;
    
    if (minimalLength <= 80) {
      return [firstCrumb, { isEllipsis: true }, { ...lastCrumb, isLast: true }];
    } else {
      // Last resort: show "... last" only (if this fits)
      const lastOnlyLength = baseLength + separatorLength + ellipsisLength + separatorLength + lastCrumb.name.length;
      if (lastOnlyLength <= 80) {
        return [{ isEllipsis: true }, { ...lastCrumb, isLast: true }];
      } else {
        // Final fallback: just show the last breadcrumb
        return [{ ...lastCrumb, isLast: true }];
      }
    }
  };

  // Loading state - show full screen spinner only for initial load
  if ((isLoading || isPrefetching) && isInitialLoad) {
    return <LoadingSpinner />;
  }

  // Error state - shown for critical errors that prevent normal operation
  if (error) {
    // Handle non-folder-specific errors (network issues, container not found, etc.)
    // Directory and file prefix errors are handled within the main table layout
    if (error !== 'DIRECTORY_NOT_FOUND' && error !== 'FILE_PREFIX_NOT_FOUND') {
      return <ErrorDisplay error={error} />;
    }
  }

  // Generate navigation breadcrumbs for current path
  const breadcrumbs = getBreadcrumbs();
  const truncatedBreadcrumbs = getTruncatedBreadcrumbs(breadcrumbs);

  /**
   * Determines which items to display based on current state
   * Handles both prefetched data (client-side) and server-side pagination
   * Applies search filtering and pagination as needed
   * @returns Array of blob items to display in the current view
   */
  const getDisplayItems = () => {
    if (currentPrefix === prefetchedPrefix && prefetchedItems.length > 0) {
      // Use prefetched data for search and pagination (better performance)
      const sortedItems = sortItems(prefetchedItems, sortField, sortDirection);
      const filteredItems = searchQuery.trim() 
        ? filterItems(sortedItems, searchQuery) 
        : sortedItems;
      
      // Always paginate, whether searching or not
      const paginatedResult = getPaginatedItems(filteredItems, currentPage, pageSize);
      return paginatedResult.items;
    } else {
      // Fallback to current items for server-side pagination
      return searchQuery.trim() 
        ? filterItems(items, searchQuery) 
        : items;
    }
  };

  // Get the final list of items to display
  const displayItems = getDisplayItems();

  // Handler functions for components
  const handleClearSearch = () => {
    setSearchQuery('');
    // For filter mode, just clear the search query - no navigation needed
  };

  const handleNavigateToBreadcrumb = (path: string) => {
    const url = new URL(window.location.href);
    const baseUrl = `${url.protocol}//${url.host}${url.pathname}`;
    const newUrl = `${baseUrl}?prefix=${path}`;
    window.history.pushState({}, '', newUrl);
    setCurrentPrefix(path);
    setCurrentPage(0);
    setContinuationTokens([]);
    loadBlobs(path, 0, []);
  };

  // Main component render - the file browser interface
  return (
    <div className="h-screen font-montserrat relative flex flex-col" style={{ 
      backgroundColor: '#293A41'
    }}>
      {/* Fixed background image with opacity overlay */}
      <div 
        className="fixed inset-0 z-0" 
        style={{
          backgroundImage: 'url(/background.jpg)',
          backgroundPosition: 'bottom',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          backgroundAttachment: 'fixed',
          opacity: 0.5
        }}
      ></div>
      
      {/* Main content container with backdrop blur effect */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8 flex flex-col h-full w-full">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 overflow-hidden flex flex-col h-full">
          
          {/* Header section with logo, title, and search */}
          <Header
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onClearSearch={handleClearSearch}
          />

          {/* Breadcrumb navigation section */}
          <div className="px-8 pb-4 text-white" style={{ background: `linear-gradient(135deg, #778788 0%, #1f2d33 100%)` }}>
            <BreadcrumbNavigation
              truncatedBreadcrumbs={truncatedBreadcrumbs}
              currentPrefix={currentPrefix}
              onNavigateToBreadcrumb={handleNavigateToBreadcrumb}
              onGoBack={handleGoBack}
              isLoading={(isLoading || isPrefetching) && !isInitialLoad}
            />
          </div>

          {/* File list container with fixed header */}
          <div className="flex-1 flex flex-col min-h-0">
            {displayItems.length === 0 ? (
              <EmptyState 
                error={error}
                searchQuery={searchQuery}
                currentPrefix={currentPrefix}
              />
            ) : (
              <FileTable
                displayItems={displayItems}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                onNavigate={handleNavigate}
                formatDate={formatDate}
                formatFileSize={formatFileSize}
              />
            )}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            hasNextPage={hasNextPage}
            displayItems={displayItems}
            searchQuery={searchQuery}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            getTotalPages={getTotalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default BlobBrowser;
